import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Polish from '@uppy/locales/lib/pl_PL'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

// Comment-file forms are usually rendered inside a Bootstrap modal loaded via
// a remote/AJAX link (data-toggle="modal"), not a Turbolinks navigation, so
// initialization can't rely on the turbolinks:load event alone. A single,
// always-on MutationObserver on document.body picks up the drag-drop area
// whenever it appears (full page load, Turbolinks visit, or AJAX-injected
// modal content) and notices when the tracked one disappears.
//
// This used to be two observers — a broad one watching document.body, and a
// narrow one (armed only once a widget was live) watching just the widget's
// own container for removal, to avoid the broad observer's callback firing
// on every one of Uppy's own internal re-renders during upload. That design
// had a race: closing the modal without saving leaves its content (and the
// old, already-initialized drag-drop area) sitting in the DOM — Bootstrap's
// dismiss just hides it. Reopening replaces the modal's innerHTML in one
// synchronous jQuery .html() call, which removes the old drag-drop area and
// inserts a fresh one in the same tick; MutationObserver callbacks run async,
// so by the time the narrow observer's callback fired (old area gone) and
// re-armed the broad one, the new area had already been inserted — the
// newly-armed observer starts watching only for *future* mutations, so it
// never saw that insertion, and the fresh widget was never initialized.
// A single observer that checks both conditions every time it fires sees
// both halves of that one DOM swap in the same callback invocation, so
// nothing gets missed.
let uppyInstance;
let trackedDragDropArea;
// Uppy's destroy() calls cancelAll() -> removeFiles(), which emits
// 'file-removed' for every file still in its state as part of teardown —
// indistinguishable, from inside the event handler, from a user clicking
// Uppy's own remove button. Without this flag, tearing down the widget after
// a successful comment submission (the container leaves the DOM) fired the
// same handler that deletes a user-removed CommentFile, deleting the file
// that had just been correctly attached to the comment.
let isBeingDestroyed = false;

function destroyUppyInstance() {
    if (uppyInstance) {
        isBeingDestroyed = true;
        uppyInstance.destroy();
        isBeingDestroyed = false;
        uppyInstance = null;
    }
    trackedDragDropArea = null;
}

function handleDomMutation() {
    if (trackedDragDropArea && !document.body.contains(trackedDragDropArea)) {
        destroyUppyInstance();
    }
    initCommentFileUppyIfPresent();
}

function initCommentFileUppyIfPresent() {
    const dragDropArea = document.querySelector('#comment-file-uppy-drag-drop-area')

    if (!dragDropArea || dragDropArea.dataset.uppyInitialized === 'true') return;
    dragDropArea.dataset.uppyInitialized = 'true';
    trackedDragDropArea = dragDropArea;

    // Scoped to this widget's own comment form. This pack is shared by every
    // view that loads it, including "add files to many" (_add_file_to_many.
    // html.erb), which injects this partial via AJAX into a modal on top of a
    // page (cases list) that has its own other forms (filters, bulk actions,
    // etc.) — a bare $("form") would match all of them, disabling every submit
    // button on the underlying page while a file uploads in the modal. The
    // drag-drop area and its <form> are siblings, not nested (both rendered
    // inside the same .user-show wrapper — the one element consistently
    // present across every view this pack is used from), so scope through
    // that shared ancestor rather than dragDropArea's own ancestry. That
    // wrapper also contains a second, hidden form (user_settings/_comment_
    // html.html.erb, toggling rich-text formatting) with its own submit
    // button — filter down to the form with the comment's own textarea so
    // that one is never mistaken for the comment form's submit button.
    //
    // Looked up fresh on every use (not captured once here) because on a
    // full, non-AJAX page load the browser parses and inserts HTML
    // incrementally: the drag-drop area (rendered earlier in the template)
    // can land in the DOM, and this MutationObserver-driven init can run,
    // before the <form> further down the same template exists yet. A stale
    // jQuery snapshot taken at that point would forever be empty, silently
    // no-op'ing every disable/relabel call — which is exactly what let a
    // comment get submitted (and its file-removed cleanup fire) before the
    // upload's own completeMultipartUpload request had even resolved.
    function getCommentForm() {
        return $(dragDropArea).closest('.user-show').find('form')
            .filter((_, form) => $(form).find('textarea').length > 0).first();
    }

    function getSubmitButton() {
        return getCommentForm().find("input[type='submit']");
    }

    // The token this widget's CommentFile records are tagged with, later
    // matched up against the comment's own authenticity_token (see Comment#
    // associate_comment_files) once the form is submitted. dragDropArea's
    // data-token attribute is a snapshot of form_authenticity_token from
    // server-render time — fine for a modal (AJAX-injected, no further JS
    // touches it), but wrong for a full page load: rails-ujs's
    // refreshCSRFTokens runs on DOMContentLoaded and overwrites every
    // form input[name="authenticity_token"] (the comment form has one added
    // automatically by form_for, plus this app's own explicit one holding
    // the same value dragDropArea's token was rendered with) to match the
    // current CSRF meta tag — silently invalidating the baked-in token
    // before the user ever gets a chance to upload a file. Reading the
    // form's own field live, at upload time (well after that refresh has
    // already happened), reflects whatever value will actually be submitted
    // with the comment, in both cases.
    function getCurrentAuthenticityToken() {
        const field = getCommentForm().find('input[name="authenticity_token"]').last();
        return field.length ? field.val() : dragDropArea.dataset.token;
    }

    let originalSubmitLabel = null;

    // Uppy's Dashboard picks a uppy-size--* class from its container's
    // measured width via a ResizeObserver, and re-measures on every one of
    // its own internal re-renders. Right next to a breakpoint, content that
    // renders at a different height/scrollbar state under one size class can
    // shift the measured width just enough to flip the class back — a
    // self-sustaining oscillation. Setting the container's width as '100%'
    // (Uppy's own default) is what makes this feedback loop possible: at
    // that point the container's width is driven by Uppy's own inner
    // content. Sizing it off the *parent* instead (100% of the parent,
    // minus a small margin so it doesn't touch the edge) keeps its width
    // tied to the stable page layout, so nothing Uppy does internally can
    // ever change it.
    dragDropArea.style.width = `calc(100% - 20px)`;

    uppyInstance = new Uppy({
        autoProceed: true,
        restrictions: {
            maxFileSize: parseInt(dragDropArea.dataset.maxFileSize),
            maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles)
        },
        locale: Polish
    })

    uppyInstance.use(Dashboard, {
        inline: true,
        target: '#comment-file-uppy-drag-drop-area',
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: '100%',
        showRemoveButtonAfterComplete: true
    })

    uppyInstance.use(AwsS3Multipart, {
        id: 'AwsS3Multipart',

        mode: 'custom',

        shouldUseMultipart: (_file) => true,

        createMultipartUpload: async (file) => {
            const response = await fetch('/s3/multipart', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    filename: file.name,
                    type: file.type
                })
            })
            const result = await response.json()
            return {
                uploadId: result.uploadId,
                key: result.key
            }
        },

        signPart: async (file, {uploadId, key, partNumber}) => {
            const res = await fetch(`/s3/multipart/${uploadId}/${partNumber}?key=${encodeURIComponent(key)}`, {
                method: 'GET'
            })

            const {url} = await res.json()
            return {
                url,
                headers: {'Content-Type': 'application/octet-stream'}
            }
        },

        completeMultipartUpload: async (file, {key, uploadId, parts}) => {
            const response = await fetch(`/s3/multipart/${uploadId}/complete?key=${encodeURIComponent(key)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    parts,
                    model: 'CommentFile',
                    token: getCurrentAuthenticityToken(),
                    uniqFilesScopeObjectType: dragDropArea.dataset.uniqFilesScopeObjectType,
                    uniqFilesScopeObjectId: dragDropArea.dataset.uniqFilesScopeObjectId
                })
            })
            const result = await response.json()

            // The file itself is already fully uploaded to S3 at this point —
            // this request only asks the server to persist the CommentFile
            // record (running e.g. the duplicate-file validation). A 4xx here
            // means that failed, so surface it instead of reporting success
            // with no record ever created.
            if (!response.ok) {
                throw new Error((result.errors || []).join(', ') || 'Nie udało się zapisać pliku')
            }

            uppyInstance.setFileState(file.id, {modelId: result.document_file_id})

            return {location: result.location}
        },

        abortMultipartUpload: async (file, {key, uploadId}) => {
            await fetch(`/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })

    uppyInstance.on('file-added', () => {
        const submitButton = getSubmitButton();
        if (originalSubmitLabel === null) originalSubmitLabel = submitButton.val();
        submitButton.prop('disabled', true);
        submitButton.val('Trwa dodawanie plików');
    });

    uppyInstance.on('complete', () => {
        const submitButton = getSubmitButton();
        submitButton.prop('disabled', false);
        submitButton.val(originalSubmitLabel);
        document.dispatchEvent(new CustomEvent('comment-files:upload-complete'));
    });

    uppyInstance.on('file-removed', (file) => {
        if (isBeingDestroyed) return;

        const submitButton = getSubmitButton();
        submitButton.prop('disabled', false);
        submitButton.val(originalSubmitLabel);

        if (file.modelId) {
            fetch('/comment_files/' + file.modelId, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })
}

document.addEventListener('turbolinks:before-render', destroyUppyInstance);
document.addEventListener('DOMContentLoaded', initCommentFileUppyIfPresent);
document.addEventListener('turbolinks:load', initCommentFileUppyIfPresent);

// The pack tag executes before <body> exists (no defer), so document.body
// isn't available yet at top level — start observing once it is.
function startObservingBody() {
    if (!document.body) {
        setTimeout(startObservingBody, 0);
        return;
    }
    new MutationObserver(handleDomMutation).observe(document.body, {childList: true, subtree: true});
    initCommentFileUppyIfPresent();
}

startObservingBody();
